

import { useState, useEffect, useRef } from "react";
import { CheckIcon } from "@heroicons/react/20/solid";
import { useAuth } from "../contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import dojoLogoLight from "../assets/dojotrack_logo_png_light.png";
import dojoLogoDark from "../assets/dojotrack_logo_png.png";
import { HoneypotField } from "../components/HoneypotField";
import {
  HONEYPOT_FIELD_NAME,
  isHoneypotTriggered,
} from "../constants/formHoneypot";

const FREE_PLAN_FEATURES = [
  "Classes, events, attendance, and student management",
  "AI retention risk scores and AI lead conversion scores",
  "Up to 10 recurring classes, with 90 days of attendance history",
  "Email messaging with 2 email templates",
  "One website form to capture leads from your site",
  "One digital waiver",
  "Data export",
] as const;

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  studioName: 100,
  studioAddress: 500,
  studioPhone: 20,
  studioEmail: 254,
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

interface StudioInfo {
  studioName: string;
  studioAddress: string;
  studioPhone: string;
  studioEmail: string;
  timeZone: string;
}

interface ValidationErrors {
  studioName?: string;
  studioAddress?: string;
  studioPhone?: string;
  studioEmail?: string;
  [key: string]: string | undefined;
}

// Utility function for consistent input styling
const getInputClassName = (baseClasses: string) => {
  return `${baseClasses} bg-white dark:bg-gray-700 text-gray-900 dark:text-white`;
};

// Input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // For text fields, preserve spaces and only remove dangerous content
  if (fieldType === 'address' || fieldType === 'studioAddress' || fieldType === 'studioName') {
    sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
    sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
    sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
    sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
    sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
  } else {
    // For other fields, use more aggressive sanitization
    sanitized = sanitized.trim();
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  }
  
  return sanitized;
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

export default function Onboarding() {
  const [currentStep, setCurrentStep] = useState(1);
  const [studioInfo, setStudioInfo] = useState<StudioInfo>({
    studioName: "",
    studioAddress: "",
    studioPhone: "",
    studioEmail: "",
    timeZone: "America/New_York",
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {}
  );
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { session, supabase } = useAuth();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  const [honeypot, setHoneypot] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  
  // Store hearAboutUs in a ref to persist across re-renders
  const hearAboutUsRef = useRef<string>("");
  const [isFreeSignup, setIsFreeSignup] = useState(
    location.state?.signupTier === 'free'
  );
  
  // Initialize hearAboutUs from location state only once
  useEffect(() => {
    if (location.state?.hearAboutUs && !hearAboutUsRef.current) {
      hearAboutUsRef.current = location.state.hearAboutUs;
    }
    if (location.state?.signupTier === 'free') {
      setIsFreeSignup(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (isFreeSignup) return;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user?.user_metadata?.signup_tier === 'free') {
        setIsFreeSignup(true);
      }
    });
  }, [isFreeSignup, supabase]);

  useEffect(() => {
    if (!session?.user?.email || studioInfo.studioEmail) return;
    setStudioInfo((prev) => ({
      ...prev,
      studioEmail: session.user.email ?? prev.studioEmail,
    }));
  }, [session?.user?.email, studioInfo.studioEmail]);

  // Fetch CSRF token on component mount
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
        if (response.ok) {
          const data = await response.json();
          if (data.token) {
            setCsrfToken(data.token);
          } else {
            logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
          }
        } else {
          logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
        }
      } catch (error) {
        logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
        // In development, generate a fallback token
        if (process.env.NODE_ENV !== 'production') {
          const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
          setCsrfToken(fallbackToken);
          console.warn('Using fallback CSRF token in development mode');
        }
      }
    };
    
    fetchCsrfToken();
  }, []);
  
  // Get hearAboutUs from ref (persists across re-renders)
  const hearAboutUs = hearAboutUsRef.current;

  // Validate origin to prevent CSRF attacks
  const validateOrigin = () => {
    const origin = window.location.origin;
    const referer = document.referrer;

    // Always allow same-page actions (user is already on the onboarding page)
    // This is safe because the user is interacting with the page they're already on
    if (window.location.pathname.includes('/onboarding')) {
      return true;
    }

    // Allow requests from same origin
    if (!referer || referer.startsWith(origin)) {
      return true;
    }

    // Allow requests from same domain (different subdomain is OK)
    // This handles cases like dojotrack.com -> app.dojotrack.com
    try {
      const originHost = new URL(origin).hostname;
      const refererHost = new URL(referer).hostname;
      
      // Extract base domain (e.g., "dojotrack.com" from "app.dojotrack.com")
      const originBaseDomain = originHost.split('.').slice(-2).join('.');
      const refererBaseDomain = refererHost.split('.').slice(-2).join('.');
      
      // If base domains match, allow it
      if (originBaseDomain === refererBaseDomain) {
        return true;
      }
    } catch (e) {
      // If URL parsing fails, fall through to other checks
    }

    // For development, allow localhost variations
    if (process.env.NODE_ENV === "development") {
      return referer.includes("localhost") || referer.includes("127.0.0.1");
    }

    return false;
  };

  // Validate CSRF token
  const validateCSRFToken = (token: string) => {
    return token && token.length > 0;
  };

  // Prevent page refresh and block F5/Ctrl+R/Cmd+R
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only show warning if user has started filling out the form but hasn't submitted
      if (
        !isProcessing &&
        (touchedFields.studioName || touchedFields.studioEmail)
      ) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // F5, Ctrl+R, Cmd+R
      if (
        e.key === "F5" ||
        ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "r")
      ) {
        if (!isProcessing) {
          e.preventDefault();
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isProcessing, touchedFields]);

  // Always disable browser back button on this page
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      window.history.pushState(null, document.title, window.location.href);
    };
    window.history.pushState(null, document.title, window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Add plan options
  const plans = [
    {
      id: "price_1Rt6VQKQZoTFogHvqWkAKnGl",
      label: "Launch - Supports up to 100 Students",
    },
    {
      id: "price_1Rt6WFKQZoTFogHviI6tHh1y",
      label: "Scale - Supports up to 200 Students",
    },
    {
      id: "price_1Rt6XuKQZoTFogHvVqKkLz6l",
      label: "Expand - Supports up to 300 Students",
    },
    {
      id: "price_1Rt6jzKQZoTFogHvyiCnAhOr",
      label: "Grow - Supports up to 500 Students",
    },
    {
      id: "price_1TFKxLKQZoTFogHv8J7FlQ8V",
      label: "Master - Supports 501+ Students",
    },
  ];

  // Comprehensive validation function
  const validateStudioInfo = (info: StudioInfo): ValidationErrors => {
    const errors: ValidationErrors = {};

    // Studio Name validation with sanitization
    const sanitizedName = sanitizeInput(info.studioName, 'studioName');
    if (!sanitizedName.trim()) {
      errors.studioName = "Studio name is required";
    } else if (sanitizedName.length < 2) {
      errors.studioName = "Studio name must be at least 2 characters";
    } else if (sanitizedName.length > MAX_LENGTHS.studioName) {
      errors.studioName = `Studio name must be less than ${MAX_LENGTHS.studioName} characters`;
    }

    // Studio Email validation with sanitization
    const sanitizedEmail = sanitizeInput(info.studioEmail, 'email');
    if (!sanitizedEmail.trim()) {
      errors.studioEmail = "Studio email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sanitizedEmail)) {
      errors.studioEmail = "Please enter a valid email address";
    } else if (sanitizedEmail.length > MAX_LENGTHS.studioEmail) {
      errors.studioEmail = `Email must be less than ${MAX_LENGTHS.studioEmail} characters`;
    }

    // Studio Phone validation with sanitization
    const sanitizedPhone = sanitizeInput(info.studioPhone, 'phone');
    if (!sanitizedPhone.trim()) {
      errors.studioPhone = "Studio phone is required";
    } else if (!/^\d{3}-\d{3}-\d{4}$/.test(sanitizedPhone)) {
      errors.studioPhone = "Please enter a valid phone number (XXX-XXX-XXXX)";
    } else if (sanitizedPhone.length > MAX_LENGTHS.studioPhone) {
      errors.studioPhone = `Phone must be less than ${MAX_LENGTHS.studioPhone} characters`;
    }

    // Studio Address validation with sanitization
    const sanitizedAddress = sanitizeInput(info.studioAddress, 'address');
    if (!sanitizedAddress.trim()) {
      errors.studioAddress = "Studio address is required";
    } else if (sanitizedAddress.length < 5) {
      errors.studioAddress = "Studio address must be at least 5 characters";
    } else if (sanitizedAddress.length > MAX_LENGTHS.studioAddress) {
      errors.studioAddress = `Address must be less than ${MAX_LENGTHS.studioAddress} characters`;
    }

    return errors;
  };

  // Check if form is valid
  const isFormValid = () => {
    const errors = validateStudioInfo(studioInfo);
    const planOk = isFreeSignup || selectedPlanId !== null;
    return (
      Object.keys(errors).length === 0 &&
      termsAccepted &&
      planOk
    );
  };

  const handleStudioInfoChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    let newValue = value;

    // Sanitize input first
    if (typeof value === 'string') {
      newValue = sanitizeInput(value, name);
    }

    if (name === "studioPhone") {
      // Remove all non-digit characters
      const digits = newValue.replace(/\D/g, "");
      // Format as XXX-XXX-XXXX
      if (digits.length <= 3) {
        newValue = digits;
      } else if (digits.length <= 6) {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else if (digits.length <= 10) {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
          6
        )}`;
      } else {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
          6,
          10
        )}`;
      }
    }

    // Validate input length
    if (MAX_LENGTHS[name as keyof typeof MAX_LENGTHS] && newValue.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
      newValue = newValue.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
    }

    setStudioInfo((prev) => ({ ...prev, [name]: newValue }));

    // Mark field as touched and validate
    setTouchedFields((prev) => ({ ...prev, [name]: true }));

    // Real-time validation for touched fields
    if (touchedFields[name]) {
      const newInfo = { ...studioInfo, [name]: newValue };
      const errors = validateStudioInfo(newInfo);
      setValidationErrors((prev) => ({ ...prev, [name]: errors[name] }));
    }
  };

  const handleFieldBlur = (fieldName: string) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    const errors = validateStudioInfo(studioInfo);
    setValidationErrors((prev) => ({
      ...prev,
      [fieldName]: errors[fieldName],
    }));
  };

  // Backend availability check for studio email
  const checkStudioEmailAvailability = async (
    email: string,
    honeypotValue: string = ""
  ): Promise<boolean> => {
    try {
      setIsCheckingEmail(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          [HONEYPOT_FIELD_NAME]: honeypotValue,
        })
      });

      if (!response.ok) {
        // Treat non-200 as exists to be safe
        return true;
      }
      const data = await response.json();
      return Boolean(data.exists);
    } catch (_) {
      // On failure, be conservative
      return true;
    } finally {
      setIsCheckingEmail(false);
    }
  };

  // Debounced email availability check when the field changes and looks valid
  useEffect(() => {
    const email = studioInfo.studioEmail;
    const isBasicValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!email || !isBasicValid) return;

    const timeout = setTimeout(async () => {
      const exists = await checkStudioEmailAvailability(email, honeypot);
      if (exists) {
        setValidationErrors((prev) => ({ ...prev, studioEmail: 'A studio with this email already exists. Please use a different email.' }));
      } else {
        // Clear only this field's error if it was the availability error
        setValidationErrors((prev) => {
          const next = { ...prev };
          if (next.studioEmail && next.studioEmail.includes('already exists')) {
            delete next.studioEmail;
          }
          return next;
        });
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [studioInfo.studioEmail, honeypot]);

  const handleCompleteFreeStudio = async () => {
    setLoadingPlanId('free');
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/create-free`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({
            name: studioInfo.studioName,
            address: studioInfo.studioAddress,
            phone: studioInfo.studioPhone,
            email: studioInfo.studioEmail,
            timezone: studioInfo.timeZone,
            hearAboutUs,
            [HONEYPOT_FIELD_NAME]: honeypot,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create free studio');
      }

      await supabase.auth.updateUser({
        data: {
          account_status: 'active',
          billing_plan: 'free',
          signup_tier: 'free',
        },
      });

      navigate('/app/dashboard');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create free studio'
      );
    } finally {
      setLoadingPlanId(null);
      setIsProcessing(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      setError(null);
      setIsProcessing(true); // Set processing state to true

      // CSRF protection
      if (!validateOrigin()) {
        setError("Invalid request origin. Please refresh the page.");
        setIsProcessing(false);
        return;
      }

      if (!validateCSRFToken(csrfToken)) {
        setError("Invalid security token. Please refresh the page.");
        setIsProcessing(false);
        return;
      }

      if (isHoneypotTriggered(honeypot)) {
        setError("Unable to process request. Please try again.");
        setIsProcessing(false);
        return;
      }

      // Client-side rate limiting
      const now = Date.now();
      if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
        setError("Please wait before submitting again");
        setIsProcessing(false);
        return;
      }

      // Validate all fields
      const errors = validateStudioInfo(studioInfo);
      setValidationErrors(errors);

      // Mark all fields as touched
      setTouchedFields({
        studioName: true,
        studioAddress: true,
        studioPhone: true,
        studioEmail: true,
      });

      // If there are validation errors, don't proceed
      if (Object.keys(errors).length > 0) {
        setIsProcessing(false);
        return;
      }

      // Server-side availability pre-check for email to avoid Stripe flow when duplicate
      const emailExists = await checkStudioEmailAvailability(
        studioInfo.studioEmail,
        honeypot
      );
      if (emailExists) {
        setValidationErrors((prev) => ({
          ...prev,
          studioEmail: 'A studio with this email already exists. Please use a different email.'
        }));
        setIsProcessing(false);
        return;
      }

      if (isFreeSignup) {
        logSecurityEvent('FREE_ONBOARDING_SUBMIT', {
          studioName: studioInfo.studioName,
          studioEmail: studioInfo.studioEmail,
        });
        setLastSubmissionTime(now);
        await handleCompleteFreeStudio();
        return;
      }

              // Create studio record before moving to next step
        try {
          const studioData = {
            name: studioInfo.studioName,
            address: studioInfo.studioAddress,
            phone: studioInfo.studioPhone,
            email: studioInfo.studioEmail,
            timezone: studioInfo.timeZone,
            hearAboutUs: hearAboutUs,
          };

          // Log security event for proceeding to plan selection
          logSecurityEvent('ONBOARDING_STEP1_COMPLETED', { 
            studioName: studioData.name,
            studioEmail: studioData.email,
            hasCsrfToken: !!csrfToken
          });

          // Store studio data in user metadata for access during webhook
          try {
            const { error: updateError } = await supabase.auth.updateUser({
              data: {
                pendingStudioData: studioData
              }
            });
            
            if (updateError) {
              console.error('Failed to store studio data in user metadata:', updateError);
              // Continue anyway - we'll use defaults in the webhook if needed
            }
          } catch (metadataError) {
            console.error('Error updating user metadata:', metadataError);
            // Continue anyway
          }
        
        // Set submission time for rate limiting
        setLastSubmissionTime(now);
        
        // Proceed with plan selection after storing studio data
        if (selectedPlanId) {
          await handlePlanSelection(selectedPlanId);
        } else {
          setError("Please select a plan to continue.");
          setIsProcessing(false);
        }
      } catch (error) {
        setIsProcessing(false);
        
        // Log security event for unexpected error
        logSecurityEvent('STUDIO_CREATION_ERROR', { 
          error: error instanceof Error ? error.message : 'Unknown error',
          studioName: studioInfo.studioName,
          studioEmail: studioInfo.studioEmail
        });
        
        setError(
          `Failed to create studio: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }
    }
  };

  const handlePlanSelection = async (planId: string) => {
    setLoadingPlanId(planId);
    setError(null);
    
    // Log security event for plan selection
    logSecurityEvent('PLAN_SELECTION_ATTEMPT', { 
      planId,
      hasCsrfToken: !!csrfToken
    });
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/platform-billing/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            priceId: planId,
            returnUrl: `${window.location.origin}/app/onboarding`,
            [HONEYPOT_FIELD_NAME]: honeypot,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        
        // Log security event for failed checkout session creation
        logSecurityEvent('CHECKOUT_SESSION_CREATION_FAILED', { 
          status: response.status,
          error: errorData.error,
          planId
        });
        
        throw new Error(errorData.error || "Failed to create checkout session");
      }

      const { url } = await response.json();
      
      // Log successful checkout session creation
      logSecurityEvent('CHECKOUT_SESSION_CREATION_SUCCESS', { 
        planId,
        returnUrl: url
      });
      
      // Clear the processing state just before redirecting
      setIsProcessing(false);
      window.location.href = url;
    } catch (error) {
      setIsProcessing(false);
      
      // Log security event for unexpected error
      logSecurityEvent('CHECKOUT_SESSION_CREATION_ERROR', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        planId
      });
      
      setError(
        error instanceof Error
          ? error.message
          : "Failed to create checkout session"
      );
    } finally {
      setLoadingPlanId(null);
    }
  };

  const renderStep = () => {
    return (
      <div className="space-y-6 relative">
        <HoneypotField value={honeypot} onChange={setHoneypot} />
        <div className="flex justify-center mb-4">
          {/* Show correct full logo for light/dark mode */}
          <img
            src={dojoLogoDark}
            alt="DojoTrack Logo"
            className="h-16 w-auto block dark:hidden"
          />
          <img
            src={dojoLogoLight}
            alt="DojoTrack Logo"
            className="h-16 w-auto hidden dark:block"
          />
        </div>
        
        {error && (
          <div className="text-red-600 dark:text-red-400 text-sm mb-2">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Studio Information */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Studio Information
            </h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Studio Name
              </label>
              <input
                type="text"
                name="studioName"
                value={studioInfo.studioName}
                onChange={handleStudioInfoChange}
                onBlur={() => handleFieldBlur("studioName")}
                className={getInputClassName(
                  `mt-1 block w-full rounded-md shadow-sm sm:text-sm border ${
                    touchedFields.studioName && validationErrors.studioName
                      ? "border-red-300 dark:border-red-600 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 dark:border-gray-600 focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                  }`
                )}
                required
              />
              {touchedFields.studioName && validationErrors.studioName && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {validationErrors.studioName}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Studio Address
              </label>
              <input
                type="text"
                name="studioAddress"
                value={studioInfo.studioAddress}
                onChange={handleStudioInfoChange}
                onBlur={() => handleFieldBlur("studioAddress")}
                className={getInputClassName(
                  `mt-1 block w-full rounded-md shadow-sm sm:text-sm border ${
                    touchedFields.studioAddress && validationErrors.studioAddress
                      ? "border-red-300 dark:border-red-600 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 dark:border-gray-600 focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                  }`
                )}
                required
              />
              {touchedFields.studioAddress && validationErrors.studioAddress && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {validationErrors.studioAddress}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Studio Phone
              </label>
              <input
                type="tel"
                name="studioPhone"
                value={studioInfo.studioPhone}
                onChange={handleStudioInfoChange}
                onBlur={() => handleFieldBlur("studioPhone")}
                className={getInputClassName(
                  `mt-1 block w-full rounded-md shadow-sm sm:text-sm border ${
                    touchedFields.studioPhone && validationErrors.studioPhone
                      ? "border-red-300 dark:border-red-600 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 dark:border-gray-600 focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                  }`
                )}
                placeholder="XXX-XXX-XXXX"
                required
              />
              {touchedFields.studioPhone && validationErrors.studioPhone && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {validationErrors.studioPhone}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Studio Email
              </label>
              <input
                type="email"
                name="studioEmail"
                value={studioInfo.studioEmail}
                onChange={handleStudioInfoChange}
                onBlur={() => handleFieldBlur("studioEmail")}
                className={getInputClassName(
                  `mt-1 block w-full rounded-md shadow-sm sm:text-sm border ${
                    touchedFields.studioEmail && validationErrors.studioEmail
                      ? "border-red-300 dark:border-red-600 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 dark:border-gray-600 focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                  }`
                )}
                required
              />
              {touchedFields.studioEmail && validationErrors.studioEmail && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {validationErrors.studioEmail}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Time Zone
              </label>
              <select
                name="timeZone"
                value={studioInfo.timeZone}
                onChange={handleStudioInfoChange}
                className={getInputClassName(
                  "mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                )}
                required
              >
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Anchorage">Alaska Time (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
              </select>
            </div>
          </div>

          {/* Right Column - Plan Selection (paid signup only) */}
          {!isFreeSignup && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Select Your Plan
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              You'll start with a 14-day free trial. After the trial period,
              you'll be automatically billed on a month-to-month basis unless you
              cancel your subscription. <strong className="text-black dark:text-[#FFFFFF]">You can upgrade, downgrade, or cancel your
              subscription at any time within the app.</strong>
              <br />
              All plans include full access to every feature—only the student
              limit differs.
            </p>
            <div className="flex flex-col gap-3">
              {plans.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => setSelectedPlanId(plan.id)}
                  className={`border rounded-md px-4 py-2 text-sm font-medium transition-colors duration-150 focus:outline-none ${
                    selectedPlanId === plan.id
                      ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 border-indigo-600 dark:border-indigo-400"
                      : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600"
                  }`}
                >
                  {plan.label}
                </button>
              ))}
            </div>
          </div>
          )}

          {isFreeSignup && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Free plan
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                No credit card required. Everything below is included from day one.
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 overflow-hidden">
              <div className="grid grid-cols-3 divide-x divide-gray-200 dark:divide-gray-600 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40">
                <div className="px-3 py-3 text-center sm:px-4">
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">50</p>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Students</p>
                </div>
                <div className="px-3 py-3 text-center sm:px-4">
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">25</p>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Leads</p>
                </div>
                <div className="px-3 py-3 text-center sm:px-4">
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">3</p>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Staff</p>
                </div>
              </div>

              <div className="p-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  What's included
                </p>
                <ul className="mt-3 space-y-3">
                  {FREE_PLAN_FEATURES.map((feature) => (
                    <li key={feature} className="flex gap-3 text-sm leading-snug text-gray-700 dark:text-gray-200">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/60">
                        <CheckIcon className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
                      </span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-600 bg-indigo-50 dark:bg-indigo-950/40 px-5 py-4">
                <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-gray-900 dark:text-white">Upgrade anytime</span>
                  {" "}for Stripe billing, SMS, kiosk, Ask Sifu, the store, and more.
                </p>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Bottom Section - Terms and Continue Button */}
        <div className="border-t pt-6 mt-8">
          <div className="flex items-center mb-6">
            <input
              id="terms"
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700"
            />
            <label
              htmlFor="terms"
              className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
            >
              I accept the{" "}
              <a
                href="https://dojotrack.com/terms/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                Terms and Conditions
              </a>
            </label>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleNext}
              className={`inline-flex items-center px-6 py-2 border text-sm font-medium rounded-md shadow-sm ${
                isFormValid()
                  ? "bg-indigo-600 dark:bg-indigo-500 text-white border-indigo-600 dark:border-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed"
              }`}
              disabled={!isFormValid() || isProcessing}
            >
              {loadingPlanId
                ? "Processing..."
                : isFreeSignup
                ? "Create free account"
                : "Continue to Payment"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="w-full max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 px-8 py-6 shadow rounded-lg">
          {renderStep()}
        </div>
      </div>
    </div>
  );
}
